/**
 * Image attachment rules for the Copilot composer.
 *
 * Every limit here mirrors one the backend enforces, byte for byte, so a file
 * the composer accepts is never rejected later by Flow
 * (`adk/agui_proxy_media.py` `MAX_IMAGE_PARTS_PER_MESSAGE`,
 * `adk/service.py` `MAX_INLINE_ATTACHMENT_BYTES`).
 * Client-side checks exist to save a round trip, not to define the contract.
 */

import { BYTES_PER_MEGABYTE } from 'shared/copilot/utils/fileSize'

export const MAX_IMAGES_PER_MESSAGE = 10
/**
 * Raw total of the images one message may carry. Flow inlines them base64 into
 * the agent session, which caps how large one event can be, and refuses a
 * larger total at send time with a 400.
 */
export const MAX_INLINE_IMAGE_BYTES_PER_MESSAGE = Math.floor(3.5 * BYTES_PER_MEGABYTE)
/**
 * One image can be no larger than a whole message may carry. Flow's 10 MB
 * upload cap (`files/service.py` `MAX_FILE_SIZE_BYTES`) only binds CSVs now.
 */
export const MAX_IMAGE_SIZE_BYTES = MAX_INLINE_IMAGE_BYTES_PER_MESSAGE

/** Interpolation values for `copilot.composer.imageAttachment.errors.*`, so the copy can never quote a stale limit. */
export const IMAGE_LIMIT_COPY_VALUES = {
  maxSizeMb: MAX_IMAGE_SIZE_BYTES / BYTES_PER_MEGABYTE,
  maxMessageSizeMb: MAX_INLINE_IMAGE_BYTES_PER_MESSAGE / BYTES_PER_MEGABYTE,
  maxImages: MAX_IMAGES_PER_MESSAGE
}

export const ACCEPTED_IMAGE_MIME_TYPES = ['image/png', 'image/jpeg'] as const
const ACCEPTED_IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg']
/** `accept` for the composer's file input — CSV rides the same button. */
export const COMPOSER_FILE_ACCEPT = '.csv,text/csv,.png,.jpg,.jpeg,image/png,image/jpeg'

export const IMAGE_VALIDATION_REASON = {
  unsupportedType: 'unsupportedType',
  tooLarge: 'tooLarge',
  empty: 'empty',
  tooMany: 'tooMany',
  duplicate: 'duplicate',
  inlineBudgetExceeded: 'inlineBudgetExceeded'
} as const

export type ImageValidationReason = (typeof IMAGE_VALIDATION_REASON)[keyof typeof IMAGE_VALIDATION_REASON]

export type ImageValidationOutcome = { ok: true } | { ok: false; reason: ImageValidationReason }

/**
 * Mirrors `isCsvFile` below: a browser's guessed MIME type is not reliable —
 * empty when it cannot infer one, or the non-standard `image/jpg` some
 * browsers/OSes report for a JPEG — so a file whose *name* carries a
 * supported image extension is accepted regardless of what MIME type, if
 * any, the browser reported for it.
 */
export const isAcceptedImageFile = (file: File): boolean =>
  (ACCEPTED_IMAGE_MIME_TYPES as readonly string[]).includes(file.type) ||
  ACCEPTED_IMAGE_EXTENSIONS.some((extension) => file.name.toLowerCase().endsWith(extension))

const MIME_TYPE_BY_EXTENSION: Record<string, string> = {
  '.png': 'image/png',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg'
}

/**
 * The MIME type to attach to a file the composer admitted.
 *
 * `isAcceptedImageFile` lets a file in on its extension alone, so `file.type`
 * can be empty or the non-standard `image/jpg` that some browsers and OSes
 * report. Neither is something the agent can act on, so the extension decides
 * whenever the browser's guess is not a type we accept. Flow's own sniffed
 * `contentType` supersedes this once the upload lands; this is what the tile
 * carries until then, and the fallback if Flow ever answers without one.
 */
export const canonicalImageMimeType = (file: File): string => {
  if ((ACCEPTED_IMAGE_MIME_TYPES as readonly string[]).includes(file.type)) {
    return file.type
  }
  const name = file.name.toLowerCase()
  const extension = ACCEPTED_IMAGE_EXTENSIONS.find((candidate) => name.endsWith(candidate))
  return extension != null ? MIME_TYPE_BY_EXTENSION[extension] : file.type
}

export const validateImageFile = (file: File): ImageValidationOutcome => {
  if (file.size === 0) {
    return { ok: false, reason: IMAGE_VALIDATION_REASON.empty }
  }
  if (!isAcceptedImageFile(file)) {
    return { ok: false, reason: IMAGE_VALIDATION_REASON.unsupportedType }
  }
  if (file.size > MAX_IMAGE_SIZE_BYTES) {
    return { ok: false, reason: IMAGE_VALIDATION_REASON.tooLarge }
  }
  return { ok: true }
}

const CSV_MIME_TYPES = ['text/csv', 'application/csv', 'text/comma-separated-values']

const isCsvFile = (file: File): boolean =>
  file.name.toLowerCase().endsWith('.csv') || CSV_MIME_TYPES.includes(file.type)

export interface PartitionedComposerFiles {
  csv: File[]
  images: File[]
  unsupported: File[]
}

/**
 * Splits one file-picker selection into the two channels the composer feeds.
 *
 * CSV is checked first: a `.csv` whose MIME the browser reports as something
 * odd must still be treated as CSV, never as an unsupported image.
 */
export const partitionComposerFiles = (files: readonly File[]): PartitionedComposerFiles => {
  const partitioned: PartitionedComposerFiles = { csv: [], images: [], unsupported: [] }

  files.forEach((file) => {
    if (isCsvFile(file)) {
      partitioned.csv.push(file)
      return
    }
    if (isAcceptedImageFile(file)) {
      partitioned.images.push(file)
      return
    }
    partitioned.unsupported.push(file)
  })

  return partitioned
}

/**
 * SHA-256 of the file's bytes, hex-encoded — the same digest Flow computes to
 * deduplicate uploads.
 *
 * Hashing before upload is what lets the composer refuse a file the user
 * already attached: without it the server would return the existing `gcsUri`
 * for the duplicate and a tile the user just added would have to disappear on
 * its own.
 *
 * Returns `null` where `crypto.subtle` is unavailable (an insecure origin, or
 * a test environment without WebCrypto). Callers treat that as "cannot tell",
 * and skip duplicate detection rather than blocking the attachment.
 */
export const computeFileContentHash = async (file: File): Promise<string | null> => {
  const subtle = globalThis.crypto?.subtle
  if (subtle == null) return null

  try {
    const digest = await subtle.digest('SHA-256', await file.arrayBuffer())
    return Array.from(new Uint8Array(digest))
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('')
  } catch {
    return null
  }
}

interface ImageContentPart {
  type: 'image'
  source: { type: string; value: string; mimeType?: string }
}

const isImageContentPart = (part: unknown): part is ImageContentPart => {
  if (part == null || typeof part !== 'object') return false
  const candidate = part as { type?: unknown; source?: unknown }
  if (candidate.type !== 'image') return false
  const source = candidate.source as { value?: unknown } | undefined
  return typeof source?.value === 'string' && source.value.length > 0
}

export interface AttachedMessageImage {
  gcsUri: string
  mimeType: string
  filename: string
}

/** Last path segment of a `gs://` URI — the only name a hydrated part carries. */
export const filenameFromGcsUri = (gcsUri: string): string => gcsUri.split('/').pop() ?? gcsUri

/**
 * Reads the image attachments off a user message's `content`.
 *
 * Live messages carry the parts the composer just built; hydrated ones carry
 * what `adk_events_to_messages` rebuilt from the persisted `file_data` parts.
 * Both arrive in the same AG-UI shape, so one reader covers the thread before
 * and after a reload.
 */
export const extractAttachedImagesFromUserContent = (content: unknown): AttachedMessageImage[] => {
  if (!Array.isArray(content)) return []

  return content.filter(isImageContentPart).map((part) => ({
    gcsUri: part.source.value,
    mimeType: part.source.mimeType ?? 'image/png',
    filename: filenameFromGcsUri(part.source.value)
  }))
}
